import { useEffect } from 'react';
import { useSettingsStore } from '../../store/useSettingsStore';
import { KeyboardShortcutsModal } from './KeyboardShortcutsModal';

interface GlobalKeyboardShortcutsProps {
  toggleMusic: () => void;
}

export function GlobalKeyboardShortcuts({ toggleMusic }: GlobalKeyboardShortcutsProps) {
  const isShortcutModalOpen = useSettingsStore((state) => state.isShortcutModalOpen);
  const setShortcutModalOpen = useSettingsStore((state) => state.setShortcutModalOpen);
  const setSettingsOpen = useSettingsStore((state) => state.setSettingsOpen);
  const setUserStatsOpen = useSettingsStore((state) => state.setUserStatsOpen);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ignore inputs
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement ||
        (e.target as HTMLElement).isContentEditable
      ) {
        return;
      }

      // Helper to check for trigger modifiers (Ctrl or Alt/Option)
      // We explicitly exclude Meta (Command) to avoid browser conflicts
      const isTrigger = e.ctrlKey || e.altKey;
      const isCmd = e.metaKey;

      // Space -> Toggle Music (Guard against any modifier)
      if (e.key === ' ' && !isTrigger && !isCmd) {
        e.preventDefault();
        toggleMusic();
      }

      // Ctrl/Alt + S -> Block save default (browser often uses Ctrl+S too)
      if (isTrigger && e.key.toLowerCase() === 's') {
        e.preventDefault();
        // Handled by PomodoroTimer logic usually
      }

      // Cmd+S -> Block Browser Save (Mac) - Just in case
      if (isCmd && e.key.toLowerCase() === 's') {
        e.preventDefault();
      }

      // Cmd+P -> Block Print
      if ((isCmd || e.ctrlKey) && e.key.toLowerCase() === 'p') {
        e.preventDefault();
        return;
      }

      // Ctrl/Alt + O -> Settings
      if (isTrigger && e.key.toLowerCase() === 'o') {
        e.preventDefault();
        setSettingsOpen(true);
      }

      // Cmd+O -> Block Browser Open (Mac)
      if (isCmd && e.key.toLowerCase() === 'o') {
        e.preventDefault();
      }

      // Ctrl/Alt + I -> User Info
      if (isTrigger && e.key.toLowerCase() === 'i') {
        e.preventDefault();
        setUserStatsOpen(true);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [toggleMusic, setSettingsOpen, setUserStatsOpen]);

  return (
    <KeyboardShortcutsModal
      isOpen={isShortcutModalOpen}
      onClose={() => setShortcutModalOpen(false)}
    />
  );
}
